import { parseLyricsText } from "@/domain/lyrics/LyricsParser";
import { artistSupabase } from "./client";
import { invokeArtistRoute } from "./functions";

const MAX_AUDIO_BYTES = 50 * 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

export interface PublishTrackInput {
	readonly ownerUserId: string;
	readonly title: string;
	readonly durationSeconds: number;
	readonly lyricsText: string;
	readonly lyricsFilename?: string;
	readonly song: File;
	readonly cover: File | null;
	readonly continents: readonly File[];
}

interface UploadedObject {
	readonly bucket: "music" | "albums-pending" | "continents-pending";
	readonly path: string;
}

export async function publishTrackToSupabase(
	input: PublishTrackInput,
): Promise<{ readonly trackId: string }> {
	const supabase = artistSupabase();
	const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
	const userId = sessionData.session?.user.id.toLowerCase() ?? null;
	if (sessionError || userId === null || userId !== input.ownerUserId.toLowerCase()) {
		throw new Error("track-upload-auth-required");
	}
	const title = input.title.trim();
	if (title.length < 1 || title.length > 200) throw new Error("track-title-invalid");
	if (
		!Number.isFinite(input.durationSeconds) ||
		input.durationSeconds <= 0 ||
		input.durationSeconds > 86_400
	) throw new Error("track-duration-invalid");
	const audio = audioDescriptor(input.song);
	if (audio === null || input.song.size < 1 || input.song.size > MAX_AUDIO_BYTES) {
		throw new Error("track-audio-invalid");
	}
	if (input.cover !== null && !validImage(input.cover)) {
		throw new Error("track-cover-invalid");
	}
	if (
		input.continents.length < 1 ||
		input.continents.length > 3 ||
		input.continents.some((file) => !validContinent(file))
	) throw new Error("track-continent-invalid");

	const uploadId = crypto.randomUUID().toLowerCase();
	const trackId = crypto.randomUUID().toLowerCase();
	const uploaded: UploadedObject[] = [];
	try {
		const audioPath = `${userId}/${trackId}/${audio.filename}`;
		await upload("music", audioPath, input.song, audio.contentType);
		uploaded.push({ bucket: "music", path: audioPath });

		let cover: { bucket: "albums-pending"; path: string; contentType: string } | undefined;
		if (input.cover !== null) {
			const extension = imageExtension(input.cover);
			if (extension === null) throw new Error("track-cover-invalid");
			const path = `${userId}/${crypto.randomUUID().toLowerCase()}.${extension}`;
			await upload("albums-pending", path, input.cover, input.cover.type);
			uploaded.push({ bucket: "albums-pending", path });
			cover = { bucket: "albums-pending", path, contentType: input.cover.type };
		}

		const continents = [];
		for (const [index, file] of input.continents.entries()) {
			const extension = continentExtension(file);
			if (extension === null) throw new Error("track-continent-invalid");
			const position = index + 1;
			const path = `${trackId}/${position}.${extension}`;
			await upload("continents-pending", path, file, file.type);
			uploaded.push({ bucket: "continents-pending", path });
			continents.push({
				bucket: "continents-pending" as const,
				path,
				contentType: file.type,
				position,
				mediaType: file.type.startsWith("video/") ? "video" as const : "image" as const,
				width: null,
				height: null,
				durationSeconds: null,
			});
		}

		const parsedLyrics = input.lyricsText.trim().length === 0
			? null
			: parseLyricsText(input.lyricsText.slice(0, 5_000), input.lyricsFilename);
		const result = await invokeArtistRoute<{
			readonly trackId: string;
			readonly status: string;
		}>("/uploads/tracks/finalize", {
			body: JSON.stringify({
				schemaVersion: 1,
				uploadId,
				trackId,
				title,
				durationSeconds: Math.round(input.durationSeconds),
				lyrics: {
					plain: parsedLyrics?.plain ?? null,
					synced: parsedLyrics?.synced ?? null,
					source: parsedLyrics === null ? null : "user_provided",
				},
				audio: {
					bucket: "music",
					path: audioPath,
					contentType: audio.contentType,
				},
				...(cover === undefined ? {} : { cover }),
				continents,
			}),
			timeoutMilliseconds: 60_000,
		});
		if (result.trackId !== trackId) throw new Error("track-upload-id-mismatch");
		return { trackId };
	} catch (error) {
		await cleanup(uploaded);
		throw error;
	}
}

async function upload(
	bucket: UploadedObject["bucket"],
	path: string,
	file: File,
	contentType: string,
): Promise<void> {
	const { error } = await artistSupabase().storage.from(bucket).upload(path, file, {
		contentType,
		cacheControl: bucket === "music" ? "31536000" : "0",
		upsert: false,
	});
	if (error) throw error;
}

async function cleanup(objects: readonly UploadedObject[]): Promise<void> {
	const grouped = new Map<UploadedObject["bucket"], string[]>();
	for (const object of objects) {
		const paths = grouped.get(object.bucket) ?? [];
		paths.push(object.path);
		grouped.set(object.bucket, paths);
	}
	await Promise.allSettled(
		[...grouped].map(([bucket, paths]) =>
			artistSupabase().storage.from(bucket).remove(paths)
		),
	);
}

function audioDescriptor(file: File): {
	readonly filename: string;
	readonly contentType: string;
} | null {
	const extension = safeExtension(file.name);
	if (file.type === "audio/mpeg" || extension === "mp3") {
		return { filename: "lossy_standard.mp3", contentType: "audio/mpeg" };
	}
	if (file.type === "audio/aac" || extension === "aac") {
		return { filename: "lossy_standard.aac", contentType: "audio/aac" };
	}
	if (
		file.type === "audio/mp4" ||
		file.type === "audio/x-m4a" ||
		extension === "m4a"
	) {
		return { filename: "lossy_standard.m4a", contentType: "audio/mp4" };
	}
	return null;
}

function validImage(file: File): boolean {
	return file.size > 0 && file.size <= MAX_IMAGE_BYTES && imageExtension(file) !== null;
}

function validContinent(file: File): boolean {
	const extension = continentExtension(file);
	if (extension === null || file.size <= 0) return false;
	return file.type.startsWith("video/")
		? file.size <= MAX_VIDEO_BYTES
		: file.size <= MAX_IMAGE_BYTES;
}

function imageExtension(file: File): string | null {
	if (file.type === "image/jpeg") return "jpg";
	if (file.type === "image/png") return "png";
	if (file.type === "image/webp") return "webp";
	return null;
}

function continentExtension(file: File): string | null {
	const image = imageExtension(file);
	if (image !== null) return image;
	const extension = safeExtension(file.name);
	if (file.type === "video/mp4" || extension === "mp4") return "mp4";
	if (file.type === "video/quicktime" || extension === "mov") return "mov";
	return null;
}

function safeExtension(filename: string): string {
	return filename.toLowerCase().match(/\.([a-z0-9]{1,8})$/u)?.[1] ?? "";
}
